import {
  ApolloClient,
  createHttpLink,
  from,
  InMemoryCache,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import { RetryLink } from '@apollo/client/link/retry';
import { relayStylePagination } from '@apollo/client/utilities';
import { CachePersistor, LocalStorageWrapper } from 'apollo3-cache-persist';

import { IFeedbackModal } from 'components/layout/FeedbackModal';
import { API_URL } from 'constants/api';
import { apolloErrorHandler } from 'utils/apolloErrorHandler';

const removeTypenameLink = removeTypenameFromVariables();

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: 30000,
  },
  attempts: (count, operation, error) =>
    !!error &&
    operation.operationName !== 'GetUserUnreadNotificationsCount' &&
    count < 5,
});

const httpLink = createHttpLink({
  uri: `${API_URL}/api/graphql`,
  credentials:
    process.env.REACT_APP_GRAPHQL_INCLUDE_CREDENTIALS ?? 'same-origin',
});

export const cache = new InMemoryCache({
  addTypename: true,
  typePolicies: {
    Query: {
      fields: {
        patientListConnection: relayStylePagination(),
        examsByUserConnection: relayStylePagination(),
        userNotificationsConnection: relayStylePagination(),
      },
    },
  },
});

export const initApollo = async (
  openFeedbackModal: (props: IFeedbackModal) => void,
  openSessionExpiryModal: () => void,
) => {
  const newPersistor = new CachePersistor({
    cache,
    storage: new LocalStorageWrapper(window.localStorage),
    debug: true,
    trigger: 'write',
  });
  await newPersistor.restore();

  const errorLink = onError(
    apolloErrorHandler(openFeedbackModal, openSessionExpiryModal),
  );

  const newClient = new ApolloClient({
    cache,
    link: from([removeTypenameLink, retryLink, errorLink, httpLink]),
    credentials:
      process.env.REACT_APP_GRAPHQL_INCLUDE_CREDENTIALS ?? 'same-origin',
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'network-only',
      },
      query: {
        fetchPolicy: 'network-only',
      },
    },
  });

  return { newClient, newPersistor };
};
